/**
 * @file UsageByEmployeeReport.tsx
 * @brief Company report — usage by employee across products.
 *
 * @references
 * - GET /api/company/reports/usage-by-employee
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Languages, Users, PenLine } from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { Avatar } from "@/components/ui/Avatar";
import { useEmployeesList } from "@/features/employees/api";
import { getInitials } from "@/shared/utils/formatters";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { rowCurrency } from "@/shared/utils/reportCurrency";
import {
  formatDurationSeconds,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { useUsageByEmployeeReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { percentChange } from "@/features/dashboard/kpiCompare";
import { previousRange } from "@/features/reports-shared/previousRange";
import { ReportShell } from "./ReportShell";

function displayName(row: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || row.email || "—";
}

export function UsageByEmployeeReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useUsageByEmployeeReport(queryRange);
  // The same-length window before, for the +/-% pills (03-10-2026).
  const prevQueryRange = useMemo(() => previousRange(queryRange), [queryRange]);
  const prevReport = useUsageByEmployeeReport(prevQueryRange);
  // Memoized so the currency grouping below is not rebuilt on every
  // render (the `?? []` literal is a fresh array each time).
  const rows = useMemo(() => report.data ?? [], [report.data]);
  // Photos come from the employees list (already signed there, and cached
  // for the Employees page), matched on user id: no report API change
  // (DES 03-10-2026: "employees kay saath unki images").
  const employees = useEmployeesList();
  const people = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );
  const usedCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          Number(r.interpretation_sessions || 0) +
            Number(r.meeting_count || 0) +
            Number(r.note_count || 0) >
          0,
      ).length,
    [rows],
  );
  const totals = useMemo(() => {
    let interp = 0;
    let meetings = 0;
    let notes = 0;
    for (const row of rows) {
      interp += Number(row.interpretation_sessions || 0);
      meetings += Number(row.meeting_count || 0);
      notes += Number(row.note_count || 0);
    }
    return { interp, meetings, notes };
  }, [rows]);

  const prev = prevReport.data;
  const prevSum = (pick: (r: (typeof rows)[number]) => unknown) =>
    (prev ?? []).reduce((n, r) => n + Number(pick(r) || 0), 0);
  const interpDelta = prev ? percentChange(totals.interp, prevSum((r) => r.interpretation_sessions)) : undefined;
  const meetingsDelta = prev ? percentChange(totals.meetings, prevSum((r) => r.meeting_count)) : undefined;
  const notesDelta = prev ? percentChange(totals.notes, prevSum((r) => r.note_count)) : undefined;

  return (
    <ReportShell
      title="Usage by employee"
      description="All team members, including those with zero activity in the range."
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "usage-by-employee",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-4">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Used Verblizr"
              icon={Users}
              // Who used it in the range out of everyone listed, as on the
              // dashboard (03-10-2026). Was the bare employee count.
              value={`${usedCount} of ${rows.length}`}
              hint="in this range"
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Interpretation"
              icon={Languages}
              value={String(totals.interp)}
              hint="Sessions"
              delta={interpDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Meeting Intel"
              icon={BoardroomTable} // DES 08-10-2026: app icon, was Presentation
              value={String(totals.meetings)}
              hint="Meetings"
              delta={meetingsDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Notes"
              icon={PenLine} // DES 08-10-2026: app icon, was NotebookPen
              value={String(totals.notes)}
              hint="Notes"
              delta={notesDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Employees"
          description="Spend is the amount billed per product, on the same basis as the Invoices page."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Employee",
              { label: "Interpretation", align: "right" },
              { label: "Meeting Intel", align: "right" },
              { label: "Notes", align: "right" },
              { label: "Total spend", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
            emptyMessage="No employees found."
          >
            {rows.map((row) => {
              const totalSpend =
                Number(row.interpretation_cost_cents || 0) +
                Number(row.meeting_cost_cents || 0) +
                Number(row.note_cost_cents || 0);
              return (
              <tr key={row.user_id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={people.get(row.user_id)?.profile_image_url}
                      initials={getInitials(row.first_name, row.last_name, row.email)}
                      gender={people.get(row.user_id)?.gender}
                      alt=""
                      size="md"
                    />
                    <div className="min-w-0">
                      <div className="font-medium">{displayName(row)}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.interpretation_sessions}
                  <span className="block text-xs text-muted-foreground">
                    {formatDurationSeconds(row.interpretation_seconds)}
                    {" · "}
                    {formatMoneyMinor(row.interpretation_cost_cents, rowCurrency(row))}
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.meeting_count}
                  <span className="block text-xs text-muted-foreground">
                    {formatDurationSeconds(row.meeting_seconds)}
                    {" · "}
                    {formatMoneyMinor(row.meeting_cost_cents, rowCurrency(row))}
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.note_count}
                  <span className="block text-xs text-muted-foreground">
                    {formatDurationSeconds(row.note_seconds)}
                    {" · "}
                    {formatMoneyMinor(row.note_cost_cents, rowCurrency(row))}
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(totalSpend, rowCurrency(row))}
                </td>
              </tr>
              );
            })}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
